/**
 * jai1 settings set <key> <value> [-j]
 * Auto-creates settings.yaml if it doesn't exist.
 * When enabling cloud, validates auth + registers project + syncs local tasks.
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { existsSync, readFileSync } from 'fs';
import { join } from 'path';
import { SettingsService } from '../../services/settings.service.js';
import { ConfigService } from '../../services/config.service.js';
import { CloudTaskProvider } from '../../services/cloud-task-provider.js';
import { TaskSchema } from '../../types/task.types.js';
import type { SettingsSetOptions } from '../../types/settings.types.js';

export function createSettingsSetCommand(): Command {
    return new Command('set')
        .description('Set a setting value (dot-notation)')
        .argument('<key>', 'Setting key (e.g., tasks.cloud)')
        .argument('<value>', 'Setting value')
        .option('-j, --json', 'Output JSON')
        .action(async (key: string, value: string, options: SettingsSetOptions) => {
            try {
                const service = new SettingsService();

                // Auto-init if settings file doesn't exist
                if (!service.exists()) {
                    await service.init(false);
                    if (!options.json) {
                        console.log(chalk.dim('📋 Auto-created settings.yaml'));
                    }
                }

                const settings = await service.set(key, value);

                if (options.json) {
                    console.log(JSON.stringify({ success: true, key, value: service.get(key), settings }, null, 2));
                    return;
                }

                console.log(chalk.green(`✅ ${key} = ${JSON.stringify(service.get(key))}`));

                // When enabling cloud, validate auth + register project + sync local tasks
                if (key === 'tasks.cloud' && service.get(key) === true) {
                    const config = await new ConfigService().load();
                    if (!config?.apiUrl || !config?.accessKey) {
                        console.log(chalk.yellow('⚠️  Chưa đăng nhập. Chạy j auth trước khi dùng cloud tasks.'));
                    } else {
                        console.log(chalk.dim('   Auth: ✓ OK'));
                        const repoUrl = service.resolveGitRepoUrl();
                        const projectId = service.getProjectId();
                        if (repoUrl && projectId) {
                            let finalProjectId = projectId;
                            try {
                                const provider = new CloudTaskProvider(config, projectId);
                                const registeredId = await provider.ensureProjectRegistered(repoUrl);
                                console.log(chalk.green('   ✓ Project registered on server'));
                                if (registeredId && registeredId !== projectId) {
                                    await service.set('tasks.projectId', registeredId);
                                    finalProjectId = registeredId;
                                }
                                console.log(chalk.dim(`   Project: ${finalProjectId}`));

                                // Auto-push local tasks to cloud
                                const tasksPath = join(process.cwd(), '.jai1', 'tasks.jsonl');
                                if (existsSync(tasksPath)) {
                                    const content = readFileSync(tasksPath, 'utf-8').trim();
                                    if (content) {
                                        const lines = content.split('\n').filter(Boolean);
                                        const tasks = lines.map((l) => TaskSchema.parse(JSON.parse(l)));
                                        if (tasks.length > 0) {
                                            console.log(chalk.dim(`   ↑ Syncing ${tasks.length} local tasks to cloud...`));
                                            const p = new CloudTaskProvider(config, finalProjectId);
                                            await p.push(tasks);
                                            console.log(chalk.green(`   ✓ ${tasks.length} tasks synced to cloud`));
                                        }
                                    }
                                }
                            } catch (error) {
                                const msg = error instanceof Error ? error.message : String(error);
                                console.log(chalk.yellow(`   ⚠️  Register failed: ${msg}`));
                                console.log(chalk.dim('   Project sẽ được đăng ký lại khi chạy lệnh task đầu tiên.'));
                            }
                        }
                    }
                }
            } catch (error) {
                if (options.json) {
                    console.log(JSON.stringify({ success: false, error: error instanceof Error ? error.message : String(error) }));
                } else {
                    console.error(chalk.red(`❌ ${error instanceof Error ? error.message : String(error)}`));
                }
                process.exit(1);
            }
        });
}
